// "My Google account": every staff member connects their OWN Google account
// here (Calendar sync + Gmail sending). Shown on the My Account page.
import React, { useCallback, useEffect, useState } from "react";
import { CalendarDays, Link2, Mail, RefreshCw, Unplug } from "lucide-react";
import { StatusPill, formatWhen, errorMessage } from "./integrationUi";

export default function GoogleConnectionPanel({ api, showNotice }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clinics, setClinics] = useState([]);
  const [providers, setProviders] = useState([]);
  const [linkForm, setLinkForm] = useState({ client_id: "", provider_id: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/google/status");
      setState(data);
    } catch (err) {
      showNotice(errorMessage(err, "Could not load Google connection status"), "error");
    }
  }, [api, showNotice]);

  useEffect(() => {
    load();
    // The OAuth consent happens in another window: refresh when we regain focus.
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const connection = state?.connection;
  const status = state?.status || "not_connected";
  const calendarReady = status === "connected" && connection?.calendar_enabled;

  useEffect(() => {
    if (!calendarReady) return;
    api.get("/clients").then(({ data }) => setClinics(Array.isArray(data) ? data : data?.items || [])).catch(() => setClinics([]));
  }, [api, calendarReady]);

  useEffect(() => {
    setProviders([]);
    if (!linkForm.client_id) return;
    api
      .get(`/v1/booking/clinics/${linkForm.client_id}/providers`)
      .then(({ data }) => setProviders(Array.isArray(data) ? data : data?.items || []))
      .catch(() => setProviders([]));
  }, [api, linkForm.client_id]);

  const act = async (fn, okMessage, failMessage) => {
    setBusy(true);
    try {
      const result = await fn();
      if (okMessage) showNotice(okMessage, "success");
      await load();
      return result;
    } catch (err) {
      showNotice(errorMessage(err, failMessage), "error");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const connect = () =>
    act(async () => {
      const { data } = await api.post("/integrations/google/connect");
      window.open(data.auth_url, "_blank", "noopener");
    }, "Google consent opened in a new window. Come back here when you are done.", "Could not start Google connection");

  const disconnect = () => {
    if (!window.confirm("Disconnect your Google account? Calendar sync and Gmail sending stop, and your calendar no longer blocks booking availability.")) return;
    return act(() => api.post("/integrations/google/disconnect"), "Google account disconnected", "Disconnect failed");
  };

  const syncNow = () =>
    act(() => api.post("/integrations/google/calendar/sync"), "Calendar synced", "Calendar sync failed");

  const link = () =>
    act(() => api.post("/integrations/google/calendar/links", linkForm), "Provider linked to your calendar", "Could not link provider");

  const unlink = (id) =>
    act(() => api.delete(`/integrations/google/calendar/links/${id}`), "Provider unlinked", "Could not unlink provider");

  if (!state) return <div className="panel glass-panel" data-testid="google-connection-panel">Loading Google connection...</div>;

  return (
    <div className="panel glass-panel" data-testid="google-connection-panel">
      <div className="panel-header">
        <h3>Google account</h3>
        <StatusPill status={status} testId="google-connection-status" />
      </div>

      {!state.configured && (
        <p data-testid="google-not-configured">
          Google sign-in is not set up for this workspace yet. A Master Administrator must add the Google OAuth app
          (GOOGLE_OAUTH_CLIENT_ID / GOOGLE_OAUTH_CLIENT_SECRET, or the Google Workspace credential in the Integration Center).
        </p>
      )}

      {connection && status !== "not_connected" && (
        <div className="compact-list">
          <div className="compact-row"><div><strong>{connection.account_email || "Google account"}</strong>
            <span>Last sync: {formatWhen(connection.last_sync_at)} - {state.events_synced} calendar event(s) pushed</span></div></div>
          <div className="compact-row"><CalendarDays size={16} /><div><strong>Calendar</strong>
            <span>{connection.calendar_enabled ? "Appointments and meetings are added to your calendar; your calendar's busy time blocks patient booking." : "Calendar access was not granted - reconnect and allow it."}</span></div></div>
          <div className="compact-row"><Mail size={16} /><div><strong>Gmail (send only)</strong>
            <span>{connection.gmail_send_enabled ? "You can send inbox emails as yourself. Incoming Gmail is not synced." : "Gmail sending was not granted - reconnect and allow it."}</span></div></div>
          {connection.last_error && status !== "needs_reauth" && (
            <div className="compact-row" data-testid="google-last-error"><div><strong>Last error</strong><span>{connection.last_error}</span></div></div>
          )}
          {status === "needs_reauth" && (
            <div className="compact-row" data-testid="google-needs-reauth"><div><strong>Reconnect required</strong>
              <span>{connection.reauth_reason || "Google no longer accepts the saved access."} Nothing syncs until you reconnect.</span></div></div>
          )}
        </div>
      )}

      <div className="button-row" style={{ marginTop: 12 }}>
        {(status === "not_connected" || status === "disconnected" || status === "needs_reauth") && (
          <button className="primary-btn small" data-testid="google-connect-button" disabled={busy || !state.configured} onClick={connect}>
            <Link2 size={14} /> {status === "needs_reauth" ? "Reconnect Google" : "Connect Google"}
          </button>
        )}
        {status === "connected" && (
          <>
            <button className="ghost-btn small" data-testid="google-sync-button" disabled={busy || !calendarReady} onClick={syncNow}>
              <RefreshCw size={14} /> Sync now
            </button>
            <button className="ghost-btn small" data-testid="google-disconnect-button" disabled={busy} onClick={disconnect}>
              <Unplug size={14} /> Disconnect
            </button>
          </>
        )}
        {status === "needs_reauth" && (
          <button className="ghost-btn small" data-testid="google-disconnect-button" disabled={busy} onClick={disconnect}>
            <Unplug size={14} /> Disconnect
          </button>
        )}
      </div>

      {calendarReady && (
        <div style={{ marginTop: 20 }} data-testid="google-provider-links">
          <div className="panel-header">
            <h4>Booking calendars</h4>
            <span>Link a clinic's booking provider to your calendar</span>
          </div>
          <p style={{ fontSize: 13 }}>
            Linked providers get their booked appointments on your Google Calendar, and your Google events (next {state.busy_days} days)
            make those times unavailable for new bookings. Only time ranges are imported, never event titles.
          </p>
          <div className="compact-list">
            {(state.provider_links || []).map((l) => (
              <div className="compact-row" key={l.id} data-testid={`google-link-${l.id}`}>
                <div><strong>{l.provider_name || l.provider_id} - {l.clinic_name || l.client_id}</strong>
                  <span>{l.busy_blocks} busy block(s) imported</span></div>
                <button className="ghost-btn small" data-testid={`google-unlink-${l.id}`} disabled={busy} onClick={() => unlink(l.id)}>Unlink</button>
              </div>
            ))}
            {!(state.provider_links || []).length && <div className="empty-cell">No providers linked yet</div>}
          </div>
          <div className="form-grid" style={{ marginTop: 8 }}>
            <label>Clinic
              <select data-testid="google-link-clinic" value={linkForm.client_id} onChange={(e) => setLinkForm({ client_id: e.target.value, provider_id: "" })}>
                <option value="">Select a clinic</option>
                {clinics.map((c) => <option key={c.id} value={c.id}>{c.clinic_name}</option>)}
              </select>
            </label>
            <label>Provider
              <select data-testid="google-link-provider" value={linkForm.provider_id} disabled={!linkForm.client_id}
                onChange={(e) => setLinkForm({ ...linkForm, provider_id: e.target.value })}>
                <option value="">Select a provider</option>
                {providers.map((p) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </label>
          </div>
          <button className="primary-btn small" data-testid="google-link-button" disabled={busy || !linkForm.client_id || !linkForm.provider_id} onClick={link}>
            Link provider
          </button>
        </div>
      )}
    </div>
  );
}
